<template>
    <div>
        <svg
            :width="width"
            :height="height"
            overflow="visible"
        >
            <path
                v-for="({d, type}, i) in lines"
                :key="i"
                :class="{line: true, highlight: type === 'value'}"
                :d="d"
            />
            <circle
                v-for="({x, y, type}, i) in points"
                :key="`point-${i}`"
                :class="{'end-point': true, highlight: type === 'value'}"
                :cx="x"
                :cy="y"
                r="3"
            />

            <circle
                v-for="(circle, i) in hitAreas"
                :key="`hover-${i}`"
                class="hover-area"
                :cx="circle.x"
                :cy="circle.y"
                r="10"
                @mouseover="onMouseEnter(circle)"
                @mouseout="onMouseLeave"
            />
            <text
                v-for="{x, label} in xAxis"
                :key="`axis-${x}`"
                class="axis-tick"
                :x="x"
                :y="height"
                dy="-0.1em"
            >{{ label }}</text>
        </svg>
        <div
            v-if="hoveredCircle"
            ref="tooltip"
            class="tooltip-content"
            :style="tooltipPos"
        >
            <slot
                name="tooltip-content"
                :value="hoveredCircle.value"
                :index="hoveredCircle.index"
            />
        </div>
    </div>
</template>

<script>
import _ from 'lodash';

const { scaleLinear } = d3;
const { extent } = d3;
const { line } = d3;

export default {
    props: {
        values: {
            type: Array,
            default: () => []
        },
        width: {
            type: Number,
            default: 100
        },
        height: {
            type: Number,
            default: 100
        },
        xProp: {
            type: String,
            default: 'x'
        },
        yProp: {
            type: String,
            default: 'y'
        }
    },
    data() {
        return {
            hoveredCircle: null,

            // Padding in px at bottom to account for x Axis
            padBottom: 14
        };
    },
    computed: {
        xAccessor() {
            return v => v[this.xProp];
        },
        yAccessor() {
            return v => v[this.yProp];
        },
        xScale() {
            return scaleLinear()
                .domain(extent(_.flatMap(this.values, arr => _.map(arr, this.xAccessor))))
                .range([10, this.width - 10]);
        },
        yScale() {
            return scaleLinear()
                .domain(extent(_.flatMap(this.values, arr => _.map(arr, this.yAccessor))))
                .range([this.height - this.padBottom - 10, 10]);
        },
        xAxis() {
            // eslint-disable-next-line no-restricted-globals
            if (isNaN(this.xScale.domain()[0]) || isNaN(this.xScale.domain()[1])) {
                return [];
            }
            return this.xScale.domain().map(x => ({
                x: this.xScale(x),
                label: x
            }));
        },
        lines() {
            if (this.values.length === 0) {
                return [];
            }

            const lineFn = line()
                .x((v) => this.xScale(this.xAccessor(v)))
                .y((v) => this.yScale(this.yAccessor(v)));

            return this.values.map(arr => ({
                d: lineFn(arr),
                type: arr[0].type
            }));
        },
        points() {
            return this.values.filter(arr => arr.length > 0).map(arr => ({
                x: this.xScale(this.xAccessor(arr[arr.length - 1])),
                y: this.yScale(this.yAccessor(arr[arr.length - 1])),
                type: arr[arr.length - 1].type
            }));
        },
        hitAreas() {
            return _.flatMap(this.values, arr => arr.map((value, index) => ({
                x: this.xScale(this.xAccessor(value)),
                y: this.yScale(this.yAccessor(value)),
                index,
                value
            })));
        },
        tooltipPos() {
            return {
                left: `${this.hoveredCircle.x + 15}px`,
                top: `${this.hoveredCircle.y - 15}px`
            };
        }
    },
    async updated() {
        await this.$nextTick();
        if (this.hoveredCircle) {
            this.fixTooltipPosition();
        }
    },
    methods: {
        onMouseEnter(circle) {
            this.hoveredCircle = circle;
        },
        onMouseLeave() {
            this.hoveredCircle = null;
        },
        fixTooltipPosition() {
            const rect = this.$refs.tooltip.getBoundingClientRect();
            if (rect.right > window.innerWidth) {
                const xAdjust = (rect.right - window.innerWidth) + 25;
                this.$refs.tooltip.style.transform = `translate(-${xAdjust}px, 25px)`;
            }
        }
    }
};
</script>

<style src="./common.chart.css" scoped></style>
<style lang="postcss" scoped>
svg {
    @apply flex-grow-0 flex-shrink-0;
    overflow: visible !important;
}

rect {
    @apply fill-current;
}

path {
    @apply stroke-current;
    fill: none;
    stroke-width: 2px;
    fill-opacity: 0;
}
circle {
    @apply fill-current;
}

path:not(.highlight), circle:not(.highlight) {
    @apply text-neutral-line;
}

text.axis-tick {
    @apply text-neutral-label fill-current text-xs;
    text-anchor: middle;
}

circle.hover-area {
    @apply cursor-pointer opacity-0;
}
</style>
